/**
 * Coffee stickers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_stickers`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const coffeeStickersPack: ToilePack = {
    slug: 'coffee-stickers',
    category: 'sticker',
    name: 'Coffee stickers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'cup', label: 'Takeaway cup', subject: 'a takeaway coffee cup with a lid and a sleeve'},
        {key: 'latte', label: 'Latte art', subject: 'a latte seen from above with a heart in the foam'},
        {key: 'moka', label: 'Moka pot', subject: 'a moka pot with steam rising from the spout'},
    ],
    styles: [
        {key: 'retro', label: 'Retro', style: 'retro sticker illustration, 1970s shapes, halftone grain, slight misregistration', palette: 'Mustard, rust, teal and cream', framing: 'Each sticker isolated with a thick white die-cut border on a plain ground'},
        {key: 'bold', label: 'Bold cartoon', style: 'bold cartoon sticker, thick black outlines, bright flat fills, slight gloss', palette: 'Bright saturated primaries with black outlines', framing: 'Each sticker isolated with a thick white die-cut border on a plain ground'},
        {key: 'minimal', label: 'Minimal line', style: 'minimal single-line sticker, one clean continuous line, no fill', palette: 'Black line only, one accent colour', framing: 'Each sticker isolated with a thin white die-cut border on a plain ground'},
    ],
    directions: ['an espresso cup instead', 'for a water bottle', 'as a to-go set'],
    directionHint: 'try: an espresso cup instead',
    seo: {
        title: 'Coffee Stickers: Takeaway Cup, Latte, Moka Pot',
        metaDescription: 'Nine coffee stickers — a takeaway cup, latte art, a moka pot — in retro, bold cartoon, and minimal line styles.',
        h1: 'Coffee stickers: takeaway cup, latte art, moka pot',
        tagline: 'Nine stickers, three coffee moments, three styles each.',
        intro: 'A takeaway coffee cup with a sleeve, a latte seen from above with a heart in the foam, and a moka pot with steam rising — three coffee stickers, die-cut and ready to print. Each rendered retro, bold cartoon, and minimal single-line. Ask for a different drink, a café-counter scene, or a French-press version, and the same three styles carry it.',
        useCases: [
            'a water bottle or laptop lid',
            'a planner or journal page',
            'a café menu or sign',
            'a gift for a coffee lover',
        ],
        faq: [
            {q: "What's in this pack?", a: 'Nine coffee stickers — a takeaway cup, a latte with heart art, and a moka pot — in retro, bold cartoon, and minimal line finishes.'},
            {q: 'Can I get a different coffee drink?', a: 'Yes — the direction field takes any variation, including a different drink, a café-counter scene, or a French-press version.'},
            {q: 'What size should I print these at?', a: 'Each sticker is a square die-cut design, so it scales cleanly from a small vinyl sticker to a full A4 sheet.'},
            {q: 'Are these photos of real coffee drinks?', a: 'No — every sticker is an original illustration, not a photograph.'},
        ],
    },
}
